import { FacilityDetailsResponse } from "@features/ax/assetRegistry/facilities/types";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { logEvent } from "@shared/analytics/analytics";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { PageHeader } from "@shared/components/PageHeader";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { TooltipProvider } from "@shared/ui/tooltip";
import {
  AlertCircle,
  AlertTriangle,
  Clock,
  DollarSign,
  FileText,
  Wrench,
  Zap,
} from "lucide-react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFacilityAlerts } from "../../../../hooks";
import { ActiveViolations } from "./activeViolationsCards";
import { OverdueInspections } from "./overdueInspectionsCards";

interface FacilityAlertsTabProps {
  facility: FacilityDetailsResponse;
}

interface AlertItemProps {
  title: string;
  subtitle?: string;
  badges?: Array<{
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
  }>;
  metadata?: string;
  actionLabel: string;
  actionVariant?: "default" | "destructive" | "outline" | "secondary" | "ghost";
  onAction?: () => void;
}

const AlertItem: React.FC<AlertItemProps> = ({
  title,
  subtitle,
  badges = [],
  metadata,
  actionLabel,
  actionVariant = "outline",
  onAction,
}) => (
  <div className="flex items-center justify-between p-3 bg-white rounded border border-gray-200">
    <div className="flex-1">
      <p className="font-medium">{title}</p>
      {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
      <div className="flex items-center gap-2 mt-1">
        {badges.map((badge, index) => (
          <Badge key={index} variant={badge.variant}>
            {badge.label}
          </Badge>
        ))}
        {metadata && <span className="text-xs text-gray-500">{metadata}</span>}
      </div>
    </div>
    {actionLabel && (
      <Button size="sm" variant={actionVariant} onClick={onAction}>
        {actionLabel}
      </Button>
    )}
  </div>
);

interface AlertCardProps {
  title: string;
  icon: React.ReactNode;
  tooltip: string;
  items: Array<{
    title: string;
    subtitle?: string;
    badges?: Array<{
      label: string;
      variant: "default" | "secondary" | "destructive" | "outline";
    }>;
    metadata?: string;
    actionLabel: string;
    actionVariant?:
      | "default"
      | "destructive"
      | "outline"
      | "secondary"
      | "ghost";
    onAction?: () => void;
  }>;
  className?: string;
}

const AlertCard: React.FC<AlertCardProps> = ({
  title,
  icon,
  tooltip,
  items,
  className = "",
}) => (
  <Card className={className}>
    <CardHeader className="pb-3">
      <div className="flex items-center gap-2">
        <CardTitle className="text-lg flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
        <CustomTooltip message={tooltip} />
      </div>
    </CardHeader>
    <CardContent>
      <div className="space-y-3">
        {items && items.length > 0 ? (
          items.map((item, index) => <AlertItem key={index} {...item} />)
        ) : (
          <p className="text-gray-500 text-center py-4">No data found</p>
        )}
      </div>
    </CardContent>
  </Card>
);

export const FacilityAlertsTab: React.FC<FacilityAlertsTabProps> = ({
  facility,
}) => {
  const { data: alertsData } = useFacilityAlerts(facility.id.toString());
  useEffect(() => {
    logEvent("alerts_tab_viewed");
  }, []);
  const navigate = useNavigate();
  const currency = useCurrency();

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "destructive" as const;
      case "medium":
        return "secondary" as const;
      case "low":
        return "default" as const;
      default:
        return "outline" as const;
    }
  };

  const getViolationStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "fixing":
        return "destructive" as const;
      case "monitor":
        return "secondary" as const;
      case "invest":
        return "default" as const;
      default:
        return "outline" as const;
    }
  };

  const criticalAssetsData =
    alertsData?.criticalAssetsOutOfService?.map((asset) => ({
      title: asset.assetName,
      subtitle: asset.assetNo,
      badges: [{ label: asset.currentStatus, variant: "secondary" as const }],
      metadata: `ETA: ${asset.eta}`,
      actionLabel: "View",
      onAction: () => {
        navigate(`/ax/asset-registry/assets/view/${asset.assetId}`);
      },
    })) || [];

  const anomaliesData =
    alertsData?.anomalies?.map((anomaly) => ({
      title: anomaly.assetName,
      subtitle: anomaly.detectedIssue,
      badges: [
        {
          label: anomaly.priority,
          variant: getPriorityColor(anomaly.priority),
        },
      ],
      metadata: anomaly.timeSinceDetection,
      actionLabel: "View",
      onAction: () => {
        navigate(`/ax/service-order/landing/view/${anomaly.serviceOrderId}`);
      },
    })) || [];

  const highMaintenanceCostData =
    alertsData?.highMaintenanceCostAssets?.map((asset) => ({
      title: asset.assetName,
      subtitle: `${
        asset.assetNo
      } • ${currency} ${asset.monthlyCost.toLocaleString()} Monthly`,
      actionLabel: "View",
      onAction: () => {
        navigate(`/ax/asset-registry/assets/view/${asset.assetId}`);
      },
    })) || [];

  const predictiveMaintenanceData =
    alertsData?.predictiveMaintenanceAlerts?.map((alert) => ({
      title: alert.assetName,
      subtitle: alert.predictedIssue,
      badges: [
        {
          label: `${alert.confidence} confidence`,
          variant: "outline" as const,
        },
      ],
      metadata: alert.timeframe,
      actionLabel: "View",
    })) || [];

  const summaryStats = [
    {
      title: "Emergency Service Orders",
      count: alertsData?.alertKpiCards?.emergencyWorkOrders?.count || 0,
      trendSummary:
        alertsData?.alertKpiCards?.emergencyWorkOrders?.label ||
        "Require immediate attention",
      icon: <Wrench className="h-8 w-8 text-red-600" />,
      tooltip:
        "Number of open, high-priority work orders requiring immediate assignment.",
    },
    {
      title: "SLA Breaches",
      count: alertsData?.alertKpiCards?.slaBreaches?.count || 0,
      trendSummary:
        alertsData?.alertKpiCards?.slaBreaches?.label ||
        "Service level violations",
      icon: <Clock className="h-8 w-8 text-yellow-600" />,
      tooltip: "Number of times Service Level Agreements were breached.",
    },
    {
      title: "Overdue Service Orders",
      count: alertsData?.alertKpiCards?.overdueTasks?.count || 0,
      trendSummary:
        alertsData?.alertKpiCards?.overdueTasks?.label || "Past due date",
      icon: <FileText className="h-8 w-8 text-blue-600" />,
      tooltip: "Total work orders past scheduled due date.",
    },
    {
      title: "Service Order Backlog",
      count: alertsData?.alertKpiCards?.workOrderBacklog?.count || 0,
      trendSummary:
        alertsData?.alertKpiCards?.workOrderBacklog?.label ||
        "Pending work orders",
      icon: <Clock className="h-8 w-8 text-purple-600" />,
      tooltip: "Summary of all pending work.",
    },
  ];

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <PageHeader
              title="Active Alerts"
              subtitle={`Critical events, potential failures, and compliance issues requiring immediate attention for ${facility.facilityName}`}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {summaryStats.map((stat, index) => (
            <QuickStatsCard key={index} {...stat} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AlertCard
            title="Critical Assets Out of Service"
            icon={<AlertTriangle className="h-5 w-5" />}
            tooltip="Assets that are currently out of service due to failures or maintenance, requiring immediate attention to restore operations."
            items={criticalAssetsData}
          />

          <AlertCard
            title="Anomalies"
            icon={<Zap className="h-5 w-5" />}
            tooltip="Sensor-detected anomalies in asset performance including vibration, temperature, pressure, and other operational parameters."
            items={anomaliesData}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AlertCard
            title="High Maintenance Cost Assets"
            icon={<DollarSign className="h-5 w-5" />}
            tooltip="Assets with unusually high maintenance costs that may benefit from replacement or enhanced maintenance strategies."
            items={highMaintenanceCostData}
          />

          <AlertCard
            title="Predictive Maintenance Alerts"
            icon={<AlertCircle className="h-5 w-5" />}
            tooltip="AI-powered predictions of potential failures based on asset performance trends and historical data."
            items={predictiveMaintenanceData}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ActiveViolations data={alertsData ?? []} />
          <OverdueInspections data={alertsData ?? []} />
        </div>
      </div>
    </TooltipProvider>
  );
};
